import { useGridFilters } from "@repo/baseCloud";
import { emptySegmentFilters, SegmentFilters } from "@repo/baseCloud/components";
import { DateTimePicker, DropDownList, FiltersV2, TextBox } from "@repo/ui";
import { useQuery } from "@tanstack/react-query";
import { useCallback } from "react";

import type { DisciplinesFilter } from "../../models";

import DisciplinesApi from "../../api/disciplinesApi";
import { QUERY_KEYS } from "../../api/queries";
import { DisciplinesGrid } from "./grid";
import {
	ACTIVE,
	activeOptions,
	AGENT,
	END_DATE_FROM,
	END_DATE_TO,
	REASONS,
	reasonOptions,
	START_DATE_FROM,
	START_DATE_TO,
	TRAINING_IDS,
	TYPES,
	typeOptions,
} from "./strings";

const initialFilters: DisciplinesFilter = {
	currentPage: 1,
	itemsPerPage: 25,
	brands: emptySegmentFilters.brandIds,
	channels: emptySegmentFilters.channelIds,
	marketers: emptySegmentFilters.marketerIds,
	states: emptySegmentFilters.stateIds,
	branches: emptySegmentFilters.branchIds,
	agent: "",
	types: [],
	active: null,
	startDateFrom: null,
	startDateTo: null,
	endDateFrom: null,
	endDateTo: null,
	reasons: [],
	trainingIds: [],
};

export function DisciplinesPage() {
	const {
		filters: draftFilters,
		setFilters: setDraftFilters,
		appliedFilters,
		setAppliedFilters,
		filtersProps,
	} = useGridFilters(initialFilters);

	const trainingsQuery = useQuery({
		queryKey: [QUERY_KEYS.Disciplines.Trainings],
		queryFn: () => DisciplinesApi.getTrainings(),
	});

	const filterChanged = (value: any, name: string) => {
		setDraftFilters((prev) => ({ ...prev, [name]: value }));
	};

	const segmentFiltersChanged = useCallback(
		(s) =>
			setDraftFilters((prev) => ({
				...prev,
				brands: s.brandIds,
				channels: s.channelIds,
				marketers: s.marketerIds,
				states: s.stateIds,
				branches: s.branchIds,
			})),
		[setDraftFilters],
	);

	return (
		<main>
			<FiltersV2 {...filtersProps}>
				<SegmentFilters onChange={segmentFiltersChanged} />
				<TextBox
					id={AGENT}
					name={AGENT}
					label="Agent (Rep Id or Name)"
					value={draftFilters.agent}
					changeHandler={filterChanged}
				/>
				<DropDownList
					id={TYPES}
					name={TYPES}
					label="Discipline Type"
					value={draftFilters.types}
					changeHandler={filterChanged}
					fullLength
					isMulti
					clearable
					values={typeOptions}
				/>
				<DropDownList
					id={ACTIVE}
					name={ACTIVE}
					label="Active/Ended"
					value={draftFilters.active}
					changeHandler={filterChanged}
					fullLength
					clearable
					values={activeOptions}
				/>
				<DateTimePicker
					id={START_DATE_FROM}
					name={START_DATE_FROM}
					label="Start Date From"
					dateOnly
					value={draftFilters.startDateFrom}
					changeHandler={filterChanged}
				/>
				<DateTimePicker
					id={START_DATE_TO}
					name={START_DATE_TO}
					label="Start Date To"
					dateOnly
					value={draftFilters.startDateTo}
					changeHandler={filterChanged}
				/>
				<DateTimePicker
					id={END_DATE_FROM}
					name={END_DATE_FROM}
					label="End Date From"
					dateOnly
					value={draftFilters.endDateFrom}
					changeHandler={filterChanged}
				/>
				<DateTimePicker
					id={END_DATE_TO}
					name={END_DATE_TO}
					label="End Date To"
					dateOnly
					value={draftFilters.endDateTo}
					changeHandler={filterChanged}
				/>
				<DropDownList
					id={REASONS}
					name={REASONS}
					label="Reason"
					value={draftFilters.reasons}
					changeHandler={filterChanged}
					fullLength
					isMulti
					clearable
					values={reasonOptions}
				/>
				<DropDownList
					id={TRAINING_IDS}
					name={TRAINING_IDS}
					label="Assigned Training"
					value={draftFilters.trainingIds}
					changeHandler={filterChanged}
					fullLength
					isMulti
					clearable
					loaded={trainingsQuery.isFetched}
					values={trainingsQuery.data ?? []}
					valueProp="id"
					textProp="name"
				/>
			</FiltersV2>
			<DisciplinesGrid filters={appliedFilters} setFilters={setAppliedFilters} />
		</main>
	);
}
